import { get, post, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type InviteScope = 'platform' | 'tenant';
export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface InviteView {
  id: string;
  email: string;
  scope: InviteScope;
  tenantId: string | null;
  roleId: string;
  roleName: string;
  status: InviteStatus;
  expiresAt: string;
  resendCount: number;
  invitedBy: string | null;
  createdAt: string;
  existingUser: boolean;
}

/** Returned from create / resend / accept-link — includes one-time accept URL. */
export interface InviteIssuedView extends InviteView {
  acceptUrl: string;
}

export interface ValidatedInvite {
  id: string;
  email: string;
  scope: InviteScope;
  tenantId: string | null;
  tenantName: string | null;
  roleId: string;
  roleName: string;
  expiresAt: string;
  existingUser: boolean;
  requiresPassword: boolean;
}

export interface AcceptInviteResult {
  status: 'accepted';
  email: string;
  existingUser: boolean;
  scope: InviteScope;
  tenantId: string | null;
}

export const inviteService = {
  validate(token: string): Promise<ValidatedInvite> {
    return post(endpoints.auth.invitesValidate, { token });
  },

  accept(payload: {
    token: string;
    fullName?: string;
    password?: string;
  }): Promise<AcceptInviteResult> {
    return post(endpoints.auth.invitesAccept, payload);
  },

  listPlatform(status?: InviteStatus): Promise<InviteView[]> {
    return get(endpoints.platformInvites.list, {
      params: status ? { status } : undefined,
    });
  },

  createPlatform(payload: { email: string; roleId: string }): Promise<InviteIssuedView> {
    return post(endpoints.platformUsers.invite, payload);
  },

  resendPlatform(inviteId: string): Promise<InviteIssuedView> {
    return post(endpoints.platformInvites.resend(inviteId));
  },

  copyLinkPlatform(inviteId: string): Promise<InviteIssuedView> {
    return post(endpoints.platformInvites.acceptLink(inviteId));
  },

  revokePlatform(inviteId: string): Promise<void> {
    return del(endpoints.platformInvites.revoke(inviteId));
  },

  listTenant(status?: InviteStatus): Promise<InviteView[]> {
    return get(endpoints.tenantInvites.list, {
      params: status ? { status } : undefined,
    });
  },

  createTenant(payload: { email: string; roleId: string }): Promise<InviteIssuedView> {
    return post(endpoints.tenantUsers.invite, payload);
  },

  resendTenant(inviteId: string): Promise<InviteIssuedView> {
    return post(endpoints.tenantInvites.resend(inviteId));
  },

  copyLinkTenant(inviteId: string): Promise<InviteIssuedView> {
    return post(endpoints.tenantInvites.acceptLink(inviteId));
  },

  revokeTenant(inviteId: string): Promise<void> {
    return del(endpoints.tenantInvites.revoke(inviteId));
  },
};
